import { ElementType } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Search } from 'lucide-react';
import {
  FacebookConversation,
  FacebookLastMessage,
  FacebookMessage,
} from '../../../utils/types';

export const fbAccent = '#1877F2';
export const fbSurface = '#f5f7fb';
export const fbBorder = 'rgba(15, 23, 32, 0.08)';
export const fbThreadBg = '#eef3fb';

export const formatMessengerTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

export const formatMessengerDay = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const sameDay =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
  if (sameDay) return 'Today';
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (sameYesterday) return 'Yesterday';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: today.getFullYear() === date.getFullYear() ? undefined : 'numeric',
  }).format(date);
};

export const getFacebookMessageStatus = (message: Partial<FacebookMessage>) =>
  (message.delivery_status || message.status || '').toString().toLowerCase();

export const getFacebookMessageAttachments = (
  message: Partial<FacebookMessage> | Partial<FacebookLastMessage>,
) => {
  const attachments = message.metadata?.attachments;
  return Array.isArray(attachments) ? attachments : [];
};

export const FacebookInboxShell = ({
  sidebar,
  children,
  hideSidebarOnMobile = false,
}: {
  sidebar: React.ReactNode;
  children: React.ReactNode;
  hideSidebarOnMobile?: boolean;
}) => (
  <Flex minH="100vh" bg={fbSurface}>
    <Box
      w={{ base: '100%', md: '360px', xl: '390px' }}
      borderRight="1px solid"
      borderColor={fbBorder}
      bg="white"
      h="100vh"
      overflow="hidden"
      display={{ base: hideSidebarOnMobile ? 'none' : 'block', md: 'block' }}
      flexShrink={0}
    >
      {sidebar}
    </Box>
    <Box flex="1" h="100vh" bg={fbThreadBg}>
      {children}
    </Box>
  </Flex>
);

export const FacebookSidebarHeader = ({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) => (
  <Flex
    px={5}
    pt={5}
    pb={4}
    align="center"
    justify="space-between"
    borderBottom="1px solid"
    borderColor={fbBorder}
  >
    <VStack align="flex-start" spacing={0.5}>
      <Text fontWeight="800" fontSize="xl" color="gray.900">
        {title}
      </Text>
      {subtitle ? (
        <Text fontSize="sm" color="gray.500">
          {subtitle}
        </Text>
      ) : null}
    </VStack>
    <HStack spacing={2}>{actions}</HStack>
  </Flex>
);

export const FacebookSearchBar = ({
  value,
  onChange,
  placeholder = 'Search conversations',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) => (
  <Box px={4} py={3} borderBottom="1px solid" borderColor={fbBorder}>
    <InputGroup>
      <InputLeftElement pointerEvents="none">
        <Icon as={Search as ElementType} color="gray.400" boxSize={4} />
      </InputLeftElement>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        bg="#f6f8fb"
        borderRadius="999px"
        borderColor="transparent"
        _focus={{
          borderColor: '#bfdbfe',
          boxShadow: '0 0 0 1px #bfdbfe',
          bg: 'white',
        }}
      />
    </InputGroup>
  </Box>
);

export const FacebookConversationRow = ({
  conversation,
  isActive,
  href,
}: {
  conversation: FacebookConversation;
  isActive?: boolean;
  href?: string;
}) => {
  const lastMessageText =
    conversation.last_message?.text ||
    conversation.last_message?.preview_text ||
    (getFacebookMessageAttachments(conversation.last_message || {}).length ? 'Attachment' : 'No messages yet');
  const lastMessageTime =
    conversation.last_message?.timestamp || conversation.last_activity;

  return (
    <Box
      // as={href ? 'a' : 'div'}
      // href={href}
      as={href ? 'a' : 'div'}
      {...(href ? { href } : {})}
      display="block"
      px={4}
      py={3.5}
      textDecoration="none"
      bg={isActive ? '#edf4ff' : 'transparent'}
      borderLeft="3px solid"
      borderLeftColor={isActive ? fbAccent : 'transparent'}
      _hover={{ bg: isActive ? '#edf4ff' : '#f8fafc' }}
      transition="background 0.18s ease"
    >
      <HStack align="flex-start" spacing={3}>
        <Avatar
          size="md"
          name={conversation.sender_name || 'User'}
          src={conversation.sender_profile_picture || undefined}
          bg={fbAccent}
          color="white"
          flexShrink={0}
        />
        <Box flex="1" minW={0}>
          <Flex justify="space-between" align="center" mb={1}>
            <Text fontWeight="700" color="gray.900" noOfLines={1}>
              {conversation.sender_name || 'Facebook user'}
            </Text>
            <Text fontSize="xs" color="gray.500" flexShrink={0} ml={2}>
              {formatMessengerTime(lastMessageTime)}
            </Text>
          </Flex>
          <Text fontSize="sm" color="gray.600" noOfLines={1}>
            {lastMessageText}
          </Text>
          <HStack spacing={2} mt={2} flexWrap="wrap">
            {conversation.requires_utility_message ? (
              <Badge colorScheme="orange" borderRadius="full" px={2.5} py={0.5}>
                Utility required
              </Badge>
            ) : null}
            {conversation.ai_chat_responses ? (
              <Badge colorScheme="blue" borderRadius="full" px={2.5} py={0.5}>
                AI on
              </Badge>
            ) : null}
          </HStack>
        </Box>
      </HStack>
    </Box>
  );
};

export const FacebookSidebarEmpty = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <VStack px={8} py={12} spacing={3} align="center" textAlign="center">
    <Text fontWeight="700" color="gray.800">
      {title}
    </Text>
    <Text fontSize="sm" color="gray.500" maxW="260px" lineHeight="1.7">
      {description}
    </Text>
  </VStack>
);

export const FacebookThreadPlaceholder = () => (
  <Flex h="100%" align="center" justify="center" px={6}>
    <VStack spacing={4} textAlign="center">
      <Box
        w="72px"
        h="72px"
        borderRadius="24px"
        bg="white"
        border="1px solid"
        borderColor={fbBorder}
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="0 16px 40px rgba(15,23,32,0.06)"
      >
        <Text fontSize="lg" fontWeight="semibold">Chat</Text>
      </Box>
      <VStack spacing={1}>
        <Text fontWeight="700" color="gray.900">
          Select a conversation
        </Text>
        <Text fontSize="sm" color="gray.500" maxW="320px" lineHeight="1.7">
          Open a conversation from the left to reply to messages, review
          context, and keep activity inside one workspace.
        </Text>
      </VStack>
    </VStack>
  </Flex>
);

export const FacebookDateSeparator = ({ label }: { label: string }) => (
  <Flex justify="center" my={4}>
    <Text
      fontSize="xs"
      fontWeight="700"
      color="gray.500"
      px={3}
      py={1.5}
      borderRadius="999px"
      bg="rgba(255,255,255,0.86)"
      border="1px solid"
      borderColor={fbBorder}
      backdropFilter="blur(8px)"
    >
      {label}
    </Text>
  </Flex>
);

export const FacebookBubble = ({
  outgoing,
  children,
  meta,
  failed,
  attachments = [],
}: {
  outgoing?: boolean;
  children: React.ReactNode;
  meta?: React.ReactNode;
  failed?: boolean;
  attachments?: Array<Record<string, any>>;
}) => (
  <Box
    maxW={{ base: '85%', md: '72%' }}
    px={4}
    py={3}
    borderRadius="20px"
    borderBottomRightRadius={outgoing ? '8px' : '20px'}
    borderBottomLeftRadius={outgoing ? '20px' : '8px'}
    bg={outgoing ? fbAccent : 'white'}
    color={outgoing ? 'white' : 'gray.900'}
    border="1px solid"
    borderColor={failed ? 'red.200' : outgoing ? 'transparent' : fbBorder}
    boxShadow="0 8px 24px rgba(15,23,32,0.06)"
  >
    <VStack align="stretch" spacing={2}>
      <Text fontSize="sm" whiteSpace="pre-wrap">
        {children}
      </Text>
      {attachments.length ? (
        <VStack align="stretch" spacing={2}>
          {attachments.map((attachment, index) =>
            attachment.url ? (
              <Box
                key={`${attachment.url}-${index}`}
                as="a"
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
                px={3}
                py={2}
                borderRadius="14px"
                bg={outgoing ? 'whiteAlpha.200' : '#f8fafc'}
                border="1px solid"
                borderColor={outgoing ? 'whiteAlpha.300' : fbBorder}
              >
                <Text fontSize="sm" fontWeight="600">
                  {attachment.title || attachment.name || `${attachment.type || 'Attachment'} attachment`}
                </Text>
                <Text fontSize="xs" color={outgoing ? 'whiteAlpha.800' : 'gray.500'}>
                  Open attachment
                </Text>
              </Box>
            ) : (
              <Box
                key={`${attachment.name || attachment.type}-${index}`}
                px={3}
                py={2}
                borderRadius="14px"
                bg={outgoing ? 'whiteAlpha.200' : '#f8fafc'}
                border="1px solid"
                borderColor={outgoing ? 'whiteAlpha.300' : fbBorder}
              >
                <Text fontSize="sm" fontWeight="600">
                  {attachment.name || `${attachment.type || 'Attachment'} attachment`}
                </Text>
              </Box>
            ),
          )}
        </VStack>
      ) : null}
      {meta ? (
        <Text
          fontSize="xs"
          color={outgoing ? 'whiteAlpha.800' : failed ? 'red.500' : 'gray.500'}
          textAlign="right"
        >
          {meta}
        </Text>
      ) : null}
    </VStack>
  </Box>
);
